import React from 'react';
import { X, LogIn, UserPlus, Gamepad2, Wallet, Gift, Play, Info } from 'lucide-react';
import MediaThumb from './MediaThumb';
import {
  GAME_CHOICE_STATE_ATTRIBUTE, HOOKS, gameChoiceHook, hook,
} from '../lib/domHooks';

/**
 * Oyuna tıklandığında açılan seçim ekranı.
 *
 *  - Giriş yapılmamışsa: Giriş Yap / Kayıt Ol / Eğlenmek için oyna (demo)
 *  - Giriş yapılmışsa:   Oyna / Demo bakiyeyle oyna
 *
 * Bahis ana bakiye + bonus bakiyesi toplamından düşülür (önce nakit, sonra
 * bonus). Bu yüzden ana butonda "gerçek bakiye" denmez ve bonus bakiyesi de
 * ekranda gösterilir — aksi hâlde oyuncu bonusuyla oynayamadığını sanıyor.
 *
 * Pencere panelden yazılan CSS'le değiştirilebilsin diye sabit kimlik taşır
 * (`data-bhs="game-choice-modal"`, alanlar `data-bhs-game-choice`; bkz.
 * lib/domHooks.js) ve renkleri satır içi stil değil `game-choice-*`
 * sınıflarıdır (App.css) — satır içi stil ancak `!important` ile ezilirdi.
 */
const GameChoiceModal = ({ game, user, balance, bonusBalance, demoAvailable, onClose,
                           onReal, onDemo, onLogin, onRegister }) => {
  if (!game) return null;

  const money = (v) => `${Number(v || 0).toFixed(2)} ₺`;
  const bonus = Number(bonusBalance || 0);
  const total = Number(balance || 0) + bonus;

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4" onClick={onClose}
         {...gameChoiceHook('overlay')}>
      <div className="game-choice-backdrop absolute inset-0" {...gameChoiceHook('backdrop')} />
      <div
        className="responsive-dialog relative w-full max-w-md pusu-panel overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
        {...hook(HOOKS.GAME_CHOICE_MODAL)}
        {...{ [GAME_CHOICE_STATE_ATTRIBUTE]: user ? 'member' : 'guest' }}
      >
        <button onClick={onClose} className="game-choice-close absolute top-3 right-3 side-icon-btn inline-flex z-10"
                aria-label="Kapat" {...gameChoiceHook('close')}>
          <X size={16} />
        </button>

        {/* Oyun görseli */}
        <div className="relative h-28 sm:h-36 overflow-hidden" {...gameChoiceHook('media')}>
          {game.img || game.image ? (
            <MediaThumb src={game.img || game.image} alt={game.name} className="w-full h-full object-cover"
                        {...gameChoiceHook('image')} />
          ) : (
            <div className="game-choice-art-fallback w-full h-full" {...gameChoiceHook('image')} />
          )}
          <div className="game-choice-shade absolute inset-0" {...gameChoiceHook('shade')} />
          <div className="absolute bottom-3 left-4 right-4" {...gameChoiceHook('heading')}>
            <div className="text-lg font-bold text-white truncate" {...gameChoiceHook('title')}>{game.name}</div>
            <div className="text-xs text-gray-400 truncate" {...gameChoiceHook('provider')}>{game.provider}</div>
          </div>
        </div>

        <div className="p-5" {...gameChoiceHook('body')}>
          {user ? (
            <>
              <div className="game-choice-box mb-4 px-3 py-2.5 rounded-xl" {...gameChoiceHook('balance')}>
                <div className="flex items-center gap-2 text-sm" {...gameChoiceHook('balance-row')}>
                  <Wallet size={15} className="text-[var(--pusu-gold)]" />
                  <span className="text-gray-400">Bakiyeniz:</span>
                  <span className="gold-text font-bold ml-auto">{money(balance)}</span>
                </div>
                {bonus > 0 && (
                  <div className="game-choice-divider-top flex items-center gap-2 text-sm mt-2 pt-2"
                       {...gameChoiceHook('bonus-row')}>
                    <Gift size={15} className="text-[var(--pusu-gold)]" />
                    <span className="text-gray-400">Bonus bakiyeniz:</span>
                    <span className="font-bold ml-auto text-[var(--pusu-gold)]">{money(bonus)}</span>
                  </div>
                )}
              </div>

              <button onClick={onReal} className="gold-btn w-full py-3.5 rounded-xl flex items-center justify-center gap-2 mb-2.5"
                      {...gameChoiceHook('play')}>
                <Play size={17} /> Oyna
              </button>

              {demoAvailable ? (
                <button onClick={onDemo} className="gold-outline-btn w-full py-3.5 rounded-xl flex items-center justify-center gap-2"
                        {...gameChoiceHook('demo')}>
                  <Gamepad2 size={17} /> Demo Bakiyeyle Oyna
                </button>
              ) : (
                <div className="game-choice-box flex items-start gap-2 text-xs text-gray-500 px-3 py-2.5 rounded-xl"
                     {...gameChoiceHook('demo-unavailable')}>
                  <Info size={14} className="mt-0.5 shrink-0 text-[var(--pusu-gold)]" />
                  <span>Bu oyun demo modunu desteklemiyor.</span>
                </div>
              )}

              {total <= 0 && (
                <div className="text-[11px] text-gray-500 text-center mt-3" {...gameChoiceHook('deposit-note')}>
                  Oynamak için hesabınıza yatırım yapmalısınız.
                </div>
              )}
            </>
          ) : (
            <>
              <p className="text-sm text-gray-400 mb-4" {...gameChoiceHook('message')}>
                Oynamak için giriş yapın, ya da hemen demo bakiye ile deneyin.
              </p>

              <button onClick={onLogin} className="gold-btn w-full py-3.5 rounded-xl flex items-center justify-center gap-2 mb-2.5"
                      {...gameChoiceHook('login')}>
                <LogIn size={17} /> Giriş Yap
              </button>
              <button onClick={onRegister} className="gold-outline-btn w-full py-3.5 rounded-xl flex items-center justify-center gap-2 mb-2.5"
                      {...gameChoiceHook('register')}>
                <UserPlus size={17} /> Kayıt Ol
              </button>

              {demoAvailable ? (
                <>
                  <div className="flex items-center gap-3 my-3" {...gameChoiceHook('divider')}>
                    <div className="flex-1 h-px bg-white/10" />
                    <span className="text-[11px] text-gray-500">veya</span>
                    <div className="flex-1 h-px bg-white/10" />
                  </div>
                  <button onClick={onDemo}
                          className="game-choice-demo w-full py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm font-semibold transition-colors"
                          {...gameChoiceHook('demo')}>
                    <Gamepad2 size={17} className="text-[var(--pusu-gold)]" /> Eğlenmek İçin Oyna
                  </button>
                  <div className="text-[11px] text-gray-500 text-center mt-2.5" {...gameChoiceHook('demo-note')}>
                    Demo bakiye ile oynanır, gerçek para kullanılmaz.
                  </div>
                </>
              ) : (
                <div className="game-choice-box flex items-start gap-2 text-xs text-gray-500 px-3 py-2.5 rounded-xl mt-1"
                     {...gameChoiceHook('demo-unavailable')}>
                  <Info size={14} className="mt-0.5 shrink-0 text-[var(--pusu-gold)]" />
                  <span>Bu oyun demo modunu desteklemiyor, oynamak için giriş yapmalısınız.</span>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GameChoiceModal;
